import React, { createContext, useContext } from 'react';
import {
  resolveGraphNodeRenderableNode,
  useWorkflowRenderCatalog,
} from '../../workflow-render-catalog';
import { WorkflowNodeShell } from './workflow-node-shell';
import type { WorkflowNodeHandles, WorkflowNodeViewData } from './types';
import { WorkflowRunStatusBadge } from '../../workflow-run-status';

export type WorkflowNodeProps = {
  node: WorkflowNodeViewData;
  handles?: WorkflowNodeHandles;
  className?: string;
  /** Optional run-status overlay badge; omitted on the editor canvas. */
  status?: string | null;
};

/**
 * Overrides a card's title and/or description for a node, where the surface can
 * resolve a friendlier label than the generic strategy — e.g. the nested
 * hierarchy resolving an agent's name from the org agent list. Returns undefined
 * to leave the strategy's own text in place.
 */
export type WorkflowNodeDisplayOverride = (
  node: WorkflowNodeViewData
) => { title?: string; description?: string } | undefined;

const WorkflowNodeDisplayOverrideContext =
  createContext<WorkflowNodeDisplayOverride | null>(null);

export const WorkflowNodeDisplayOverrideProvider =
  WorkflowNodeDisplayOverrideContext.Provider;

/** The display override in scope, so a non-card surface (e.g. the hierarchy's
 * node inspector header) resolves the same friendly title the cards do. */
export function useWorkflowNodeDisplayOverride(): WorkflowNodeDisplayOverride | null {
  return useContext(WorkflowNodeDisplayOverrideContext);
}

/**
 * Renders a workflow graph node using the strategy registered for `node.type`.
 */
export const WorkflowNode: React.FC<WorkflowNodeProps> = ({
  node,
  handles,
  className,
  status,
}) => {
  const catalog = useWorkflowRenderCatalog();
  const strategy = resolveGraphNodeRenderableNode(catalog, node.type).view;
  const override = useContext(WorkflowNodeDisplayOverrideContext)?.(node);
  const title = override?.title ?? strategy.getTitle(node);
  const description = override?.description ?? strategy.getDescription?.(node);
  const icon = strategy.renderIcon?.(node);
  const body = strategy.renderBody(node);

  return (
    <WorkflowNodeShell
      title={title}
      description={description}
      icon={icon}
      accent={strategy.accent}
      badge={strategy.badge}
      handles={handles}
      className={className}
    >
      {body}
      {status && (
        <div className="mt-2">
          <WorkflowRunStatusBadge status={status} />
        </div>
      )}
    </WorkflowNodeShell>
  );
};
